import { Currency } from "@redotech/money/currencies";
import { safeParseEnum } from "@redotech/util/enum";
import { TemporalDayOfWeek } from "@redotech/util/temporal";
import { Team } from "../team";
import { WeightUnit } from "../weight-conversion";
import {
  AddressPropertiesSegment,
  AddressPropertyField,
  CartValueKind,
  CartValueSegment,
  CheckoutProbabilitySegment,
  CheckoutTreeNarySegment,
  CrossBorderShipmentSegment,
  CustomerReturnRateSegment,
  CustomerTagsSegment,
  CustomerTagsSegmentNodeIdentifier,
  DatetimeRangeSegment,
  DeliveryDistanceMilesSegment,
  DeliveryLocationSegment,
  HistoricalOrderValueSegment,
  ItemQuantitySegment,
  LineItemPropertiesSegment,
  OrderCountSegment,
  OriginLocationSegment,
  PoBoxShipmentSegment,
  ProductCollectionsSegment,
  ProductMetafieldsSegment,
  ProductSkuSegment,
  ProductTagsSegment,
  ProductTagsSegmentNodeIdentifier,
  ProductVendorSegment,
  Segment,
  SegmentableCheckoutCharacteristic,
  SegmentListMultipleItemMembershipOperator,
  SegmentListSingleItemMembershipOperator,
  SegmentNumberOperator,
  SegmentStringOperator,
  SegmentTimeOfDayOperator,
  ShipmentWeightSegment,
  TimeOfDaySegment,
} from "./checkout-tree";

export const segmentableCharacteristicLabel: Record<
  SegmentableCheckoutCharacteristic,
  string
> = {
  [SegmentableCheckoutCharacteristic.DELIVERY_DISTANCE_MILES]:
    "Delivery distance",
  [SegmentableCheckoutCharacteristic.ORDER_COUNT]: "Order count",
  [SegmentableCheckoutCharacteristic.HISTORICAL_ORDER_VALUE]:
    "Historical order value",
  [SegmentableCheckoutCharacteristic.DELIVERY_LOCATION]: "Delivery location",
  [SegmentableCheckoutCharacteristic.DELIVERY_LOCATION_V02262026]:
    "Delivery location",
  [SegmentableCheckoutCharacteristic.CUSTOMER_TAGS]: "Customer tags",
  [SegmentableCheckoutCharacteristic.PRODUCT_TAGS]: "Product tags",
  [SegmentableCheckoutCharacteristic.PRODUCT_VENDOR]: "Product vendor",
  [SegmentableCheckoutCharacteristic.PRODUCT_SKU]: "Product SKU",
  [SegmentableCheckoutCharacteristic.ITEM_QUANTITY]: "Item quantity",
  [SegmentableCheckoutCharacteristic.ORIGIN_LOCATION]: "Origin location",
  [SegmentableCheckoutCharacteristic.CUSTOMER_RETURN_RATE]:
    "Customer return rate",
  [SegmentableCheckoutCharacteristic.TIME_OF_DAY]: "Time of day",
  [SegmentableCheckoutCharacteristic.DATETIME_RANGE]: "Datetime range",
  [SegmentableCheckoutCharacteristic.CHECKOUT_PROBABILITY]:
    "Checkout probability score",
  [SegmentableCheckoutCharacteristic.PRODUCT_COLLECTIONS]:
    "Product collections",
  [SegmentableCheckoutCharacteristic.LINE_ITEM_PROPERTIES]:
    "Line item properties",
  [SegmentableCheckoutCharacteristic.PRODUCT_METAFIELDS]: "Product metafields",
  [SegmentableCheckoutCharacteristic.CART_VALUE]: "Cart value",
  [SegmentableCheckoutCharacteristic.SHIPMENT_WEIGHT]: "Shipment weight",
  [SegmentableCheckoutCharacteristic.CROSS_BORDER_SHIPMENT]:
    "Cross-border shipment",
  [SegmentableCheckoutCharacteristic.PO_BOX_SHIPMENT]: "PO box shipment",
  [SegmentableCheckoutCharacteristic.ADDRESS_PROPERTIES]: "Address properties",
};

export type NarySegmentCharacteristic =
  SegmentableCheckoutCharacteristic.DELIVERY_LOCATION_V02262026;

export const narySegmentCharacteristics: ReadonlySet<SegmentableCheckoutCharacteristic> =
  new Set<NarySegmentCharacteristic>([
    SegmentableCheckoutCharacteristic.DELIVERY_LOCATION_V02262026,
  ]);

export function getDefaultNarySegment(
  characteristic: SegmentableCheckoutCharacteristic,
  edgeIds: string[],
): CheckoutTreeNarySegment {
  switch (characteristic) {
    case SegmentableCheckoutCharacteristic.DELIVERY_LOCATION_V02262026:
      return {
        segmentableCharacteristic: characteristic,
        conditions: edgeIds.map((edgeId) => ({ edgeId, locationRules: [] })),
      };
    default:
      throw new Error(
        `No default n-ary segment for characteristic: ${characteristic}`,
      );
  }
}

const defaultDeliveryDistanceMilesSegment: DeliveryDistanceMilesSegment = {
  segmentableCharacteristic:
    SegmentableCheckoutCharacteristic.DELIVERY_DISTANCE_MILES,
  miles: 100,
  operator: SegmentNumberOperator.LESS_THAN_OR_EQUAL,
};

const defaultOrderCountSegment: OrderCountSegment = {
  segmentableCharacteristic: SegmentableCheckoutCharacteristic.ORDER_COUNT,
  numOrders: 1,
  operator: SegmentNumberOperator.LESS_THAN,
};

function getDefaultHistoricalOrderValueSegment(
  shopCurrency: Currency,
): HistoricalOrderValueSegment {
  return {
    segmentableCharacteristic:
      SegmentableCheckoutCharacteristic.HISTORICAL_ORDER_VALUE,
    amount: 100,
    // Source of truth — denominated in the shop's currency at creation time.
    currency: shopCurrency,
    operator: SegmentNumberOperator.GREATER_THAN_OR_EQUAL,
  };
}

const defaultDeliveryLocationSegment: DeliveryLocationSegment = {
  segmentableCharacteristic:
    SegmentableCheckoutCharacteristic.DELIVERY_LOCATION,
  locationCountry: {
    countries: ["US"],
    operator: SegmentListSingleItemMembershipOperator.IN,
  },
};

const defaultCustomerTagsSegment: CustomerTagsSegment = {
  segmentableCharacteristic: SegmentableCheckoutCharacteristic.CUSTOMER_TAGS,
  tags: [],
  operator: SegmentListMultipleItemMembershipOperator.ANY_IN,
  customerTagsSegmentNodeIdentifier:
    CustomerTagsSegmentNodeIdentifier.CUSTOMER_TAGS_SEGMENT_NODE_ONE,
};

const defaultProductVendorSegment: ProductVendorSegment = {
  segmentableCharacteristic: SegmentableCheckoutCharacteristic.PRODUCT_VENDOR,
  vendors: [],
  operator: SegmentListSingleItemMembershipOperator.IN,
  cartOperator: SegmentListMultipleItemMembershipOperator.ANY_IN,
};

const defaultProductSkuSegment: ProductSkuSegment = {
  segmentableCharacteristic: SegmentableCheckoutCharacteristic.PRODUCT_SKU,
  skus: [],
  operator: SegmentListSingleItemMembershipOperator.IN,
  cartOperator: SegmentListMultipleItemMembershipOperator.ANY_IN,
  requireExactMatch: true,
};

const defaultProductTagsSegment: ProductTagsSegment = {
  segmentableCharacteristic: SegmentableCheckoutCharacteristic.PRODUCT_TAGS,
  tags: [],
  operator: SegmentListMultipleItemMembershipOperator.ANY_IN,
  cartOperator: SegmentListMultipleItemMembershipOperator.ANY_IN,
  productTagsSegmentNodeIdentifier:
    ProductTagsSegmentNodeIdentifier.PRODUCT_TAGS_SEGMENT_NODE_ONE,
};

const defaultItemQuantitySegment: ItemQuantitySegment = {
  segmentableCharacteristic: SegmentableCheckoutCharacteristic.ITEM_QUANTITY,
  quantity: 2,
  operator: SegmentNumberOperator.LESS_THAN_OR_EQUAL,
  exclusions: { skus: ["x-redo"] },
};

const defaultOriginLocationSegment: OriginLocationSegment = {
  segmentableCharacteristic: SegmentableCheckoutCharacteristic.ORIGIN_LOCATION,
  originAddress: {
    address1: null,
    address2: null,
    city: null,
    province: null,
    postalCode: null,
    country: null,
  },
};

const defaultCustomerReturnRateSegment: CustomerReturnRateSegment = {
  segmentableCharacteristic:
    SegmentableCheckoutCharacteristic.CUSTOMER_RETURN_RATE,
  returnRatePercentage: 50,
  operator: SegmentNumberOperator.GREATER_THAN_OR_EQUAL,
};

function getDefaultTimeOfDaySegment(
  ianaTimezone?: string | undefined,
): TimeOfDaySegment {
  return {
    segmentableCharacteristic: SegmentableCheckoutCharacteristic.TIME_OF_DAY,
    time: { hh: 15, mm: 0 },
    weekdays: [
      TemporalDayOfWeek.MONDAY,
      TemporalDayOfWeek.TUESDAY,
      TemporalDayOfWeek.WEDNESDAY,
      TemporalDayOfWeek.THURSDAY,
      TemporalDayOfWeek.FRIDAY,
      TemporalDayOfWeek.SATURDAY,
      TemporalDayOfWeek.SUNDAY,
    ],
    ianaTimezone: ianaTimezone ?? "America/New_York",
    operator: SegmentTimeOfDayOperator.BEFORE,
  };
}

function getDefaultDatetimeRangeSegment(
  ianaTimezone?: string | undefined,
): DatetimeRangeSegment {
  const now = Temporal.Now.plainDateTimeISO().with({
    hour: 0,
    minute: 0,
    second: 0,
    millisecond: 0,
    microsecond: 0,
  });
  return {
    segmentableCharacteristic: SegmentableCheckoutCharacteristic.DATETIME_RANGE,
    startDateTime: now.toString(),
    endDateTime: now.toString(),
    ianaTimezone: ianaTimezone ?? "America/New_York",
  };
}

export enum DefaultCheckoutProbabilityBucket {
  COLD = 0,
  COOL = 1,
  WARM = 2,
  HOT = 3,
}

// lower thresholds
export const defaultCheckoutProbabilityLowerThresholds: Record<
  DefaultCheckoutProbabilityBucket,
  number
> = {
  [DefaultCheckoutProbabilityBucket.COLD]: 0,
  [DefaultCheckoutProbabilityBucket.COOL]: 15,
  [DefaultCheckoutProbabilityBucket.WARM]: 35,
  [DefaultCheckoutProbabilityBucket.HOT]: 70,
};

// upper thresholds
export const defaultCheckoutProbabilityUpperThresholds: Record<
  DefaultCheckoutProbabilityBucket,
  number
> = {
  [DefaultCheckoutProbabilityBucket.COLD]: 15,
  [DefaultCheckoutProbabilityBucket.COOL]: 35,
  [DefaultCheckoutProbabilityBucket.WARM]: 70,
  [DefaultCheckoutProbabilityBucket.HOT]: 100,
};

export const defaultCheckoutProbabilityExpressions: Record<
  DefaultCheckoutProbabilityBucket,
  CheckoutProbabilitySegment["expressions"]
> = {
  // Cold bucket: 0% <= p < 15%
  [DefaultCheckoutProbabilityBucket.COLD]: [
    {
      probability:
        defaultCheckoutProbabilityLowerThresholds[
          DefaultCheckoutProbabilityBucket.COLD
        ],
      operator: SegmentNumberOperator.GREATER_THAN_OR_EQUAL,
    },
    {
      probability:
        defaultCheckoutProbabilityUpperThresholds[
          DefaultCheckoutProbabilityBucket.COLD
        ],
      operator: SegmentNumberOperator.LESS_THAN,
    },
  ],
  // Cool bucket: 15% <= p < 35%
  [DefaultCheckoutProbabilityBucket.COOL]: [
    {
      probability:
        defaultCheckoutProbabilityLowerThresholds[
          DefaultCheckoutProbabilityBucket.COOL
        ],
      operator: SegmentNumberOperator.GREATER_THAN_OR_EQUAL,
    },
    {
      probability:
        defaultCheckoutProbabilityUpperThresholds[
          DefaultCheckoutProbabilityBucket.COOL
        ],
      operator: SegmentNumberOperator.LESS_THAN,
    },
  ],
  // Warm bucket: 35% <= p < 70%
  [DefaultCheckoutProbabilityBucket.WARM]: [
    {
      probability:
        defaultCheckoutProbabilityLowerThresholds[
          DefaultCheckoutProbabilityBucket.WARM
        ],
      operator: SegmentNumberOperator.GREATER_THAN_OR_EQUAL,
    },
    {
      probability:
        defaultCheckoutProbabilityUpperThresholds[
          DefaultCheckoutProbabilityBucket.WARM
        ],
      operator: SegmentNumberOperator.LESS_THAN,
    },
  ],
  // Hot bucket: 70% <= p <= 100%
  [DefaultCheckoutProbabilityBucket.HOT]: [
    {
      probability:
        defaultCheckoutProbabilityLowerThresholds[
          DefaultCheckoutProbabilityBucket.HOT
        ],
      operator: SegmentNumberOperator.GREATER_THAN_OR_EQUAL,
    },
    {
      probability:
        defaultCheckoutProbabilityUpperThresholds[
          DefaultCheckoutProbabilityBucket.HOT
        ],
      operator: SegmentNumberOperator.LESS_THAN_OR_EQUAL,
    },
  ],
};

export function getDefaultCheckoutProbabilityBucket(
  probability: number,
): DefaultCheckoutProbabilityBucket {
  for (const bucket of Object.values(DefaultCheckoutProbabilityBucket)) {
    if (typeof bucket !== "number") {
      continue;
    }

    const lowerThreshold = defaultCheckoutProbabilityLowerThresholds[bucket];
    const upperThreshold = defaultCheckoutProbabilityUpperThresholds[bucket];

    // Special case for HOT bucket to include probability === 100
    if (bucket === DefaultCheckoutProbabilityBucket.HOT) {
      if (probability >= lowerThreshold && probability <= upperThreshold) {
        return bucket;
      }
    } else if (probability >= lowerThreshold && probability < upperThreshold) {
      return bucket;
    }
  }

  throw new Error("No bucket found for probability");
}

const defaultIntentSegment: CheckoutProbabilitySegment = {
  segmentableCharacteristic:
    SegmentableCheckoutCharacteristic.CHECKOUT_PROBABILITY,
  expressions:
    defaultCheckoutProbabilityExpressions[
      DefaultCheckoutProbabilityBucket.WARM
    ],
};

const defaultProductCollectionsSegment: ProductCollectionsSegment = {
  segmentableCharacteristic:
    SegmentableCheckoutCharacteristic.PRODUCT_COLLECTIONS,
  collectionIds: [],
  collectionListOperator: SegmentListMultipleItemMembershipOperator.ANY_IN,
  cartOperator: SegmentListMultipleItemMembershipOperator.ANY_IN,
};

const defaultLineItemPropertiesSegment: LineItemPropertiesSegment = {
  segmentableCharacteristic:
    SegmentableCheckoutCharacteristic.LINE_ITEM_PROPERTIES,
  properties: [],
  propertiesListOperator: SegmentListMultipleItemMembershipOperator.ANY_IN,
  cartOperator: SegmentListMultipleItemMembershipOperator.ANY_IN,
};

const defaultProductMetafieldsSegment: ProductMetafieldsSegment = {
  segmentableCharacteristic:
    SegmentableCheckoutCharacteristic.PRODUCT_METAFIELDS,
  metafields: [],
  operator: SegmentListMultipleItemMembershipOperator.ANY_IN,
  cartOperator: SegmentListMultipleItemMembershipOperator.ANY_IN,
};

const defaultCartValueSegment: CartValueSegment = {
  segmentableCharacteristic: SegmentableCheckoutCharacteristic.CART_VALUE,
  value: { amount: 50 },
  operator: SegmentNumberOperator.GREATER_THAN_OR_EQUAL,
  valueKind: CartValueKind.DISCOUNTED,
};

const defaultShipmentWeightSegment: ShipmentWeightSegment = {
  segmentableCharacteristic: SegmentableCheckoutCharacteristic.SHIPMENT_WEIGHT,
  weight: 5,
  unit: WeightUnit.KILOGRAMS,
  operator: SegmentNumberOperator.GREATER_THAN_OR_EQUAL,
};

const defaultCrossBorderShipmentSegment: CrossBorderShipmentSegment = {
  segmentableCharacteristic:
    SegmentableCheckoutCharacteristic.CROSS_BORDER_SHIPMENT,
};

const defaultPoBoxShipmentSegment: PoBoxShipmentSegment = {
  segmentableCharacteristic: SegmentableCheckoutCharacteristic.PO_BOX_SHIPMENT,
};

const defaultAddressPropertiesSegment: AddressPropertiesSegment = {
  segmentableCharacteristic:
    SegmentableCheckoutCharacteristic.ADDRESS_PROPERTIES,
  rules: [
    {
      field: AddressPropertyField.ADDRESS1,
      operator: SegmentStringOperator.CONTAINS,
      value: "",
    },
  ],
};

export const CHECKOUT_PROBABILITY_LINE_ITEM_PROPERTY_KEY =
  "_redo_intent_engine_checkout_probability";

export function getDefaultSegments(
  team?: Team | undefined,
): Record<
  Exclude<SegmentableCheckoutCharacteristic, NarySegmentCharacteristic>,
  Segment
> {
  const ianaTimezone = team?._shopify?.iana_timezone ?? undefined;
  const shopCurrency =
    safeParseEnum(team?._shopify?.currency, Currency) ?? Currency.USD;

  return {
    [SegmentableCheckoutCharacteristic.DELIVERY_DISTANCE_MILES]:
      defaultDeliveryDistanceMilesSegment,
    [SegmentableCheckoutCharacteristic.ORDER_COUNT]: defaultOrderCountSegment,
    [SegmentableCheckoutCharacteristic.HISTORICAL_ORDER_VALUE]:
      getDefaultHistoricalOrderValueSegment(shopCurrency),
    [SegmentableCheckoutCharacteristic.DELIVERY_LOCATION]:
      defaultDeliveryLocationSegment,
    [SegmentableCheckoutCharacteristic.CUSTOMER_TAGS]:
      defaultCustomerTagsSegment,
    [SegmentableCheckoutCharacteristic.PRODUCT_VENDOR]:
      defaultProductVendorSegment,
    [SegmentableCheckoutCharacteristic.PRODUCT_SKU]: defaultProductSkuSegment,
    [SegmentableCheckoutCharacteristic.PRODUCT_TAGS]: defaultProductTagsSegment,
    [SegmentableCheckoutCharacteristic.ITEM_QUANTITY]:
      defaultItemQuantitySegment,
    [SegmentableCheckoutCharacteristic.ORIGIN_LOCATION]:
      defaultOriginLocationSegment,
    [SegmentableCheckoutCharacteristic.CUSTOMER_RETURN_RATE]:
      defaultCustomerReturnRateSegment,
    [SegmentableCheckoutCharacteristic.TIME_OF_DAY]:
      getDefaultTimeOfDaySegment(ianaTimezone),
    [SegmentableCheckoutCharacteristic.DATETIME_RANGE]:
      getDefaultDatetimeRangeSegment(ianaTimezone),
    [SegmentableCheckoutCharacteristic.CHECKOUT_PROBABILITY]:
      defaultIntentSegment,
    [SegmentableCheckoutCharacteristic.PRODUCT_COLLECTIONS]:
      defaultProductCollectionsSegment,
    [SegmentableCheckoutCharacteristic.LINE_ITEM_PROPERTIES]:
      defaultLineItemPropertiesSegment,
    [SegmentableCheckoutCharacteristic.PRODUCT_METAFIELDS]:
      defaultProductMetafieldsSegment,
    [SegmentableCheckoutCharacteristic.CART_VALUE]: defaultCartValueSegment,
    [SegmentableCheckoutCharacteristic.SHIPMENT_WEIGHT]:
      defaultShipmentWeightSegment,
    [SegmentableCheckoutCharacteristic.CROSS_BORDER_SHIPMENT]:
      defaultCrossBorderShipmentSegment,
    [SegmentableCheckoutCharacteristic.PO_BOX_SHIPMENT]:
      defaultPoBoxShipmentSegment,
    [SegmentableCheckoutCharacteristic.ADDRESS_PROPERTIES]:
      defaultAddressPropertiesSegment,
  };
}
